import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { getCurrentUser, getStoredTokens, refreshSession } from '../services/authService';

// Extract basic claims from ID token to optimistically set user while network request resolves
function decodeIdToken(idToken) {
    if (!idToken) return null;
    try {
        const payload = idToken.split('.')[1];
        const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(decodeURIComponent(json.split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')));
    } catch {
        return null;
    }
}

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const checkSession = useCallback(async () => {
        try {
            const { accessToken, idToken } = getStoredTokens();
            if (!accessToken) {
                setLoading(false);
                return;
            }

            // Optimistic local decode (avoid flicker on refresh)
            if (idToken) {
                const claims = decodeIdToken(idToken);
                if (claims && claims.email) {
                    setUser((prev) => prev || { email: claims.email, name: claims.name || claims.email });
                }
            }

            const userResponse = await getCurrentUser();
            if (userResponse) {
                const userAttributes = {};
                userResponse.UserAttributes.forEach(attr => {
                    userAttributes[attr.Name] = attr.Value;
                });
                setUser(userAttributes);
            }
        } catch (error) {
            // Network / token error -> attempt refresh only on 401-like conditions
            const isUnauthorized = error?.$metadata?.httpStatusCode === 401 || error?.name === 'NotAuthorizedException';
            if (isUnauthorized) {
                try {
                    await refreshSession();
                    const userResponse = await getCurrentUser();
                    if (userResponse) {
                        const userAttributes = {};
                        userResponse.UserAttributes.forEach(attr => {
                            userAttributes[attr.Name] = attr.Value;
                        });
                        setUser(userAttributes);
                    }
                } catch (refreshError) {
                    console.error('Session refresh failed:', refreshError);
                    setError(refreshError);
                    // Do not clear user immediately; allow UI to decide navigation.
                    setUser(null);
                }
            } else {
                // 403 / other errors: keep current user (authorization issue for a resource, not session)
                setError(error);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkSession();
        // Only run on mount or when checkSession identity changes
    }, [checkSession]);

    const value = {
        user,
        loading,
        error,
        setUser,
        refreshUser: checkSession
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}; 